/**
 * GoalOutcomeCard — end-of-lesson reflection card.
 *
 * Stage-2 P6 (Feb 18, 2026). Doctrine:
 *   • "A non-win is not failure. It is direction."
 *   • Four locked questions in exact order.
 *   • Three outcome branches (yes / partly / not_yet).
 *   • No automatic progression — instructor explicitly confirms
 *     next-step intent.
 *   • Calm slate palette; no red, no celebration spam.
 *   • Drive Mode parked-only on submit (server enforces 409).
 */
import { useMemo, useState } from "react";
import axios from "axios";
import { Sparkles, Check, X, ArrowRight, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  QUESTIONS,
  resolveOutcome,
  HEADLINES,
} from "../../lib/goalOutcomeCopy";
import useDriveModeState from "../../hooks/useDriveModeState";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// B0 Step 4 — Quiet-refresher inline links surfaced when the lesson
// outcome is `not_yet` or `partly`. Doctrine card (§A1/§A2) wires
// understanding hubs A1 (Roundabouts) and A2 (Junctions) into the
// Goal Outcome Loop carry-forward branches; A3 (Road positioning)
// is intentionally NOT in this branch — it links from Training
// Mode position-relevant focuses instead.
const NOT_YET_QUIET_REFRESHERS = [
  { path: "/roundabouts", label: "Roundabouts" },
  { path: "/junctions",   label: "Junctions"   },
];


const HOMEWORK_FIELDS = [
  { key: "happened_today",      label: "What happened today" },
  { key: "needs_strengthening", label: "What needs strengthening" },
  { key: "watch_before_next",   label: "What to watch / think about before next lesson" },
  { key: "coming_next_preview", label: "Coming next" },
];


export default function GoalOutcomeCard({
  lessonId, learnerId, surface = "dark", onComplete,
}) {
  const [answers, setAnswers] = useState({
    four_times: null, was_a_win: null,
    ready_to_step_up: null, strengthen_again: null,
  });
  const [overrides, setOverrides] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const { movingState: localMovingState } = useDriveModeState();
  const movingState = localMovingState || "unknown";

  const preview = useMemo(() => resolveOutcome(answers), [answers]);
  const allAnswered = QUESTIONS.every((q) => answers[q.key] !== null);

  if (!lessonId || !learnerId) return null;

  const isDark = surface === "dark";
  const wrapCls = isDark
    ? "bg-slate-800/70 border-slate-700 text-slate-200"
    : "bg-slate-50 border-slate-200 text-slate-700";
  const dividerCls = isDark ? "border-slate-700/60" : "border-slate-200/60";
  const mutedCls   = isDark ? "text-slate-400" : "text-slate-500";

  const setAnswer = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  const submit = async () => {
    if (!allAnswered || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/goal-outcome/submit`,
        {
          learner_id:   learnerId,
          lesson_id:    lessonId,
          answers,
          homework:     overrides,
          moving_state: movingState,
        },
        { withCredentials: true },
      );
      setSaved(r.data);
      if (onComplete) onComplete(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(d?.message || (d?.code === "drive_mode_glance_only"
        ? "We can capture this when you're parked."
        : "Couldn't save just yet."));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (intentOverride) => {
    if (!saved?.outcome?.id || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/goal-outcome/${saved.outcome.id}/instructor-confirm`,
        {
          instructor_confirmed_intent: true,
          override_intent: intentOverride || null,
          moving_state: movingState,
        },
        { withCredentials: true },
      );
      setSaved(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail?.message || "Couldn't confirm just yet.");
    } finally {
      setBusy(false);
    }
  };

  // ── Phase 2: confirmation view ───────────────────────────────────
  if (saved) {
    const outcome = saved.outcome;
    const hw      = saved.homework;
    return (
      <div
        data-testid="goal-outcome-card"
        data-phase="confirmed"
        data-outcome={outcome.outcome}
        data-intent={outcome.next_step_intent}
        className={`rounded-xl border ${wrapCls} p-4`}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 opacity-70" />
          <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
            Goal Outcome — {outcome.outcome.replace("_", " ")}
          </span>
        </div>
        <p
          data-testid="goal-outcome-headline"
          className="text-sm font-semibold mb-3"
        >
          {outcome.headline}
        </p>

        <ul className="space-y-2 mb-3" data-testid="goal-outcome-homework">
          {HOMEWORK_FIELDS.map((f) => (
            <li key={f.key} data-testid={`homework-${f.key}`}>
              <div className={`text-[11px] uppercase tracking-wide ${mutedCls}`}>
                {f.label}
              </div>
              <div className="text-[13px] leading-snug">
                {hw?.[f.key]}
              </div>
            </li>
          ))}
        </ul>

        <div className={`border-t ${dividerCls} pt-3`}>
          <div className={`text-[11px] uppercase tracking-wide ${mutedCls} mb-2`}>
            Instructor — confirm next step (optional)
          </div>
          {outcome.instructor_confirmed_intent ? (
            <p
              data-testid="goal-outcome-confirmed"
              className="text-[12px] inline-flex items-center gap-1.5"
            >
              <Check className="w-3 h-3 opacity-70" />
              Confirmed · {outcome.next_step_intent.replace("_", " ")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <ConfirmButton
                testid="goal-outcome-confirm-as-is"
                onClick={() => confirm(null)} busy={busy}
                Icon={Check} label="Confirm as suggested"
              />
              <ConfirmButton
                testid="goal-outcome-confirm-strengthen"
                onClick={() => confirm("strengthen")} busy={busy}
                Icon={ArrowRight} label="Strengthen instead"
              />
              <ConfirmButton
                testid="goal-outcome-confirm-calmer"
                onClick={() => confirm("calmer_repeat")} busy={busy}
                Icon={ArrowRight} label="Calmer repeat"
              />
            </div>
          )}
          {error ? (
            <p
              data-testid="goal-outcome-error"
              className={`text-[11px] mt-2 ${mutedCls}`}
            >{error}</p>
          ) : null}
        </div>

        <p className={`mt-3 text-[11px] ${mutedCls}`}>
          Advisory only — no auto-advance. You confirm the next step.
        </p>

        {/* B0 Step 4 — quiet refreshers. Inline understanding-hub
            links surfaced only when the outcome carries forward
            (`not_yet` or `partly`). No new component, no rebuild —
            just a calm rail that connects reflection to refresher. */}
        {(outcome.outcome === "not_yet" || outcome.outcome === "partly") ? (
          <div
            data-testid="goal-outcome-quiet-refreshers"
            data-outcome={outcome.outcome}
            className={`mt-3 pt-3 border-t ${dividerCls}`}
          >
            <p className={`text-[10px] uppercase tracking-wide ${mutedCls} mb-1.5`}>
              Quiet refreshers
            </p>
            <div className="flex flex-wrap gap-x-3 gap-y-1.5">
              {NOT_YET_QUIET_REFRESHERS.map((r) => (
                <Link
                  key={r.path}
                  to={r.path}
                  data-testid={`goal-outcome-refresher-${r.path.slice(1)}`}
                  className={`inline-flex items-center gap-1 text-[12px] font-medium underline-offset-2 hover:underline ${
                    isDark ? "text-slate-200" : "text-slate-700"
                  }`}
                >
                  <ExternalLink className="w-3 h-3 opacity-70" />
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // ── Phase 1: capture view ────────────────────────────────────────
  return (
    <div
      data-testid="goal-outcome-card"
      data-phase="capture"
      data-preview-outcome={preview.outcome}
      data-moving-state={movingState}
      className={`rounded-xl border ${wrapCls} p-4`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 opacity-70" />
        <span className="text-[11px] font-semibold uppercase tracking-wide opacity-70">
          End-of-lesson reflection
        </span>
      </div>
      <p className={`text-[11px] ${mutedCls} mb-3`}>
        Four calm questions. No grading.
      </p>

      <ul className="space-y-3 mb-3">
        {QUESTIONS.map((q) => (
          <li key={q.key} data-testid={`goal-question-${q.key}`}>
            <div className="text-[13px] leading-snug mb-1.5">{q.text}</div>
            <div className="flex gap-2">
              <AnswerButton
                testid={`goal-answer-${q.key}-yes`}
                active={answers[q.key] === true}
                onClick={() => setAnswer(q.key, true)}
                Icon={Check} label="Yes"
              />
              <AnswerButton
                testid={`goal-answer-${q.key}-no`}
                active={answers[q.key] === false}
                onClick={() => setAnswer(q.key, false)}
                Icon={X} label="No"
              />
            </div>
          </li>
        ))}
      </ul>

      {allAnswered ? (
        <div
          data-testid="goal-outcome-preview"
          data-outcome={preview.outcome}
          className={`text-[12px] mb-3 border-t ${dividerCls} pt-2`}
        >
          <span className={`uppercase tracking-wide text-[10px] ${mutedCls}`}>
            Looks like
          </span>
          <span className="ml-2 font-semibold">{preview.outcome_label}</span>
          <span className={`ml-2 ${mutedCls}`}>· {preview.next_step_label}</span>
        </div>
      ) : null}

      <details className="text-[11px] mb-3">
        <summary className={`cursor-pointer ${mutedCls}`}>
          Adjust homework (optional)
        </summary>
        <div className="mt-2 space-y-2">
          {HOMEWORK_FIELDS.map((f) => (
            <label key={f.key} className="block">
              <span className={`block text-[10px] uppercase tracking-wide ${mutedCls} mb-1`}>
                {f.label}
              </span>
              <input
                type="text"
                data-testid={`goal-homework-${f.key}`}
                value={overrides[f.key] || ""}
                onChange={(e) =>
                  setOverrides((o) => ({ ...o, [f.key]: e.target.value }))
                }
                placeholder={HEADLINES[preview.outcome] || ""}
                className={`w-full text-[12px] rounded-md px-2 py-1.5 border ${
                  isDark
                    ? "bg-slate-900/70 border-slate-700 text-slate-200"
                    : "bg-white border-slate-200 text-slate-800"
                }`}
              />
            </label>
          ))}
        </div>
      </details>

      <button
        type="button"
        onClick={submit}
        data-testid="goal-outcome-submit"
        disabled={!allAnswered || busy}
        className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-md px-3 py-2"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Check className="w-4 h-4" />
        )}
        Capture this reflection
      </button>

      {error ? (
        <p
          data-testid="goal-outcome-error"
          className={`text-[11px] mt-2 ${mutedCls}`}
        >{error}</p>
      ) : null}
    </div>
  );
}


function AnswerButton({ testid, active, onClick, Icon, label }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[12px] font-semibold px-3 py-1 rounded-md border ${
        active
          ? "bg-slate-200 text-slate-900 border-slate-300"
          : "bg-transparent text-slate-400 border-slate-700 hover:text-slate-200"
      }`}
    >
      <Icon className="w-3 h-3 opacity-70" />
      {label}
    </button>
  );
}


function ConfirmButton({ testid, onClick, busy, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 px-2.5 py-1.5 rounded-md"
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}
